import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  InputAdornment,
  FormHelperText,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  }
}));

const TransactionEditPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [formData, setFormData] = useState({
    reference: '',
    type: 'payment',
    bookingId: '',
    bookingReference: '',
    userId: '',
    userName: '',
    partnerId: '',
    partnerName: '',
    amount: '',
    currency: 'EUR',
    status: 'pending',
    paymentMethod: 'card',
    cardLast4: '',
    description: '',
  });
  
  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    fetchTransaction();
    fetchRelatedData();
  }, [id]);

  const fetchTransaction = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const transactionDoc = doc(firestore, 'transactions', id);
      const transactionSnapshot = await getDoc(transactionDoc);
      
      if (transactionSnapshot.exists()) {
        const transactionData = {
          id: transactionSnapshot.id,
          ...transactionSnapshot.data()
        };
        setFormData(transactionData);
        setOriginalData(transactionData);
      } else {
        // Si la transaction n'existe pas, utiliser des données de démonstration
        if (id === 'TRX001') {
          const demoTransaction = { 
            id: 'TRX001', 
            reference: 'PAY-2025-001',
            type: 'payment',
            bookingId: 'B001',
            bookingReference: 'RES-2025-001',
            userId: 'U001',
            userName: 'Alice Martin',
            amount: 120.50,
            currency: 'EUR',
            status: 'completed',
            paymentMethod: 'card',
            cardLast4: '4242',
            description: 'Paiement pour réservation Paris-Lyon',
            createdAt: '2025-03-15T10:30:00',
            updatedAt: '2025-03-15T10:30:00'
          };
          setFormData(demoTransaction);
          setOriginalData(demoTransaction);
        } else if (id === 'TRX004') {
          const demoTransaction = { 
            id: 'TRX004', 
            reference: 'PAY-2025-003',
            type: 'payment',
            bookingId: 'B003',
            bookingReference: 'RES-2025-003',
            userId: 'U003',
            userName: 'Sophie Dubois',
            amount: 145.20,
            currency: 'EUR',
            status: 'pending',
            paymentMethod: 'bank_transfer',
            description: 'Paiement en attente pour réservation Lyon-Grenoble',
            createdAt: '2025-04-02T11:15:00',
            updatedAt: '2025-04-02T11:15:00'
          };
          setFormData(demoTransaction);
          setOriginalData(demoTransaction);
        } else {
          setSnackbar({
            open: true,
            message: 'Transaction non trouvée',
            severity: 'error'
          });
          setTimeout(() => {
            navigate('/admin/transactions');
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error fetching transaction:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de la transaction',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedData = async () => {
    try {
      // Simulation de récupération des données depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par des appels à l'API
      
      // Données de démonstration pour les réservations
      const demoBookings = [
        { id: 'B001', reference: 'RES-2025-001', userId: 'U001', userName: 'Alice Martin', amount: 120.50 },
        { id: 'B002', reference: 'RES-2025-002', userId: 'U002', userName: 'Thomas Bernard', amount: 85.75 },
        { id: 'B003', reference: 'RES-2025-003', userId: 'U003', userName: 'Sophie Dubois', amount: 145.20 },
        { id: 'B004', reference: 'RES-2025-004', userId: 'U004', userName: 'Pierre Lambert', amount: 175.00 },
        { id: 'B005', reference: 'RES-2025-005', userId: 'U005', userName: 'Julie Moreau', amount: 65.30 },
      ];
      setBookings(demoBookings);
      
      // Données de démonstration pour les utilisateurs
      const demoUsers = [
        { id: 'U001', name: 'Alice Martin', email: 'alice.martin@example.com' },
        { id: 'U002', name: 'Thomas Bernard', email: 'thomas.bernard@example.com' },
        { id: 'U003', name: 'Sophie Dubois', email: 'sophie.dubois@example.com' },
        { id: 'U004', name: 'Pierre Lambert', email: 'pierre.lambert@example.com' },
        { id: 'U005', name: 'Julie Moreau', email: 'julie.moreau@example.com' },
      ];
      setUsers(demoUsers);
      
      // Données de démonstration pour les partenaires
      const demoPartners = [
        { id: 'P001', name: 'Entreprise ABC', email: 'contact@abc.com' },
        { id: 'P002', name: 'Société XYZ', email: 'info@xyz.com' },
        { id: 'P003', name: 'Compagnie 123', email: 'support@123.com' },
      ];
      setPartners(demoPartners);
      
    } catch (error) {
      console.error('Error fetching related data:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données associées',
        severity: 'error'
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'bookingId' && value) {
      const selectedBooking = bookings.find(booking => booking.id === value);
      if (selectedBooking) {
        setFormData({
          ...formData,
          [name]: value,
          bookingReference: selectedBooking.reference,
          userId: selectedBooking.userId,
          userName: selectedBooking.userName,
          amount: selectedBooking.amount.toString()
        });
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else if (name === 'userId' && value) {
      const selectedUser = users.find(user => user.id === value);
      if (selectedUser) {
        setFormData({
          ...formData,
          [name]: value,
          userName: selectedUser.name
        });
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else if (name === 'partnerId' && value) {
      const selectedPartner = partners.find(partner => partner.id === value);
      if (selectedPartner) {
        setFormData({
          ...formData,
          [name]: value,
          partnerName: selectedPartner.name
        });
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Effacer l'erreur lorsque l'utilisateur modifie le champ
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleCancel = () => {
    navigate(`/admin/transactions/${id}`);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.amount) {
      newErrors.amount = 'Le montant est requis';
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Le montant doit être un nombre positif';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    
    if (formData.paymentMethod === 'card' && !formData.cardLast4) {
      newErrors.cardLast4 = 'Les 4 derniers chiffres de la carte sont requis';
    } else if (formData.paymentMethod === 'card' && !/^\d{4}$/.test(formData.cardLast4)) {
      newErrors.cardLast4 = 'Doit contenir exactement 4 chiffres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Veuillez corriger les erreurs dans le formulaire',
        severity: 'error'
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Préparer les données pour l'envoi
      const transactionData = {
        ...formData,
        amount: parseFloat(formData.amount),
        updatedAt: new Date().toISOString()
      };
      
      // Si le statut a changé à "completed", ajouter la date de complétion
      if (formData.status === 'completed' && originalData.status !== 'completed') {
        transactionData.completedAt = new Date().toISOString();
      }
      
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const transactionDoc = doc(firestore, 'transactions', id);
      await updateDoc(transactionDoc, transactionData);
      
      setSnackbar({
        open: true,
        message: 'Transaction mise à jour avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la page de détail après un court délai
      setTimeout(() => {
        navigate(`/admin/transactions/${id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error updating transaction:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour de la transaction',
        severity: 'error'
      });
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Vérifier si la transaction est modifiable
  const isEditable = originalData && originalData.status !== 'completed';

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleCancel}
        style={{ marginBottom: 16 }}
      >
        Retour aux détails
      </Button>
      
      <Typography variant="h4" component="h1" gutterBottom>
        Modifier la transaction
      </Typography>
      
      {!isEditable && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          Cette transaction est terminée et ne peut plus être modifiée.
        </Alert>
      )}
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Informations de base
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="reference"
                  label="Référence"
                  variant="outlined"
                  fullWidth
                  value={formData.reference}
                  disabled
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="type-label">Type de transaction</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    label="Type de transaction"
                    disabled={true} // Le type ne peut pas être modifié
                  >
                    <MenuItem value="payment">Paiement</MenuItem>
                    <MenuItem value="refund">Remboursement</MenuItem>
                    <MenuItem value="commission_payment">Paiement de commission</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Divider className={classes.divider} />
            
            {(formData.type === 'payment' || formData.type === 'refund') && (
              <>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Détails de la réservation et du client
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="bookingReference"
                      label="Référence de réservation"
                      variant="outlined"
                      fullWidth
                      value={formData.bookingReference}
                      disabled
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="userName"
                      label="Client"
                      variant="outlined"
                      fullWidth
                      value={formData.userName}
                      disabled
                    />
                  </Grid>
                </Grid>
              </>
            )}
            
            {formData.type === 'commission_payment' && (
              <>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Détails du partenaire
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      name="partnerName"
                      label="Partenaire"
                      variant="outlined"
                      fullWidth
                      value={formData.partnerName}
                      disabled
                    />
                  </Grid>
                </Grid>
              </>
            )}
            
            <Divider className={classes.divider} />
            
            <Typography variant="h6" className={classes.sectionTitle}>
              Détails financiers
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="amount"
                  label="Montant"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0.01,
                    step: 0.01
                  }}
                  disabled={!isEditable}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="currency-label">Devise</InputLabel>
                  <Select
                    labelId="currency-label"
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    label="Devise"
                    disabled={!isEditable}
                  >
                    <MenuItem value="EUR">Euro (€)</MenuItem>
                    <MenuItem value="USD">Dollar américain ($)</MenuItem>
                    <MenuItem value="GBP">Livre sterling (£)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="status-label">Statut</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Statut"
                    disabled={!isEditable}
                  >
                    <MenuItem value="pending">En attente</MenuItem>
                    <MenuItem value="completed">Terminée</MenuItem>
                    <MenuItem value="failed">Échouée</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="payment-method-label">Méthode de paiement</InputLabel>
                  <Select
                    labelId="payment-method-label"
                    id="paymentMethod"
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    label="Méthode de paiement"
                    disabled={!isEditable}
                  >
                    <MenuItem value="card">Carte bancaire</MenuItem>
                    <MenuItem value="paypal">PayPal</MenuItem>
                    <MenuItem value="bank_transfer">Virement bancaire</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {formData.paymentMethod === 'card' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    name="cardLast4"
                    label="4 derniers chiffres de la carte"
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.cardLast4}
                    onChange={handleChange}
                    error={!!errors.cardLast4}
                    helperText={errors.cardLast4 || "Ex: 4242"}
                    inputProps={{
                      maxLength: 4,
                      pattern: '[0-9]*'
                    }}
                    disabled={!isEditable}
                  />
                </Grid>
              )}
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  multiline
                  rows={3}
                  disabled={!isEditable}
                />
              </Grid>
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                className={classes.button}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
                disabled={submitting || !isEditable}
                startIcon={submitting ? <CircularProgress size={24} /> : <SaveIcon />}
              >
                {submitting ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        )}
      </Paper>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionEditPage;
